// frontend/src/pages/DocumentSearchPage.jsx

/**
 * Страница «Поиск по документу».
 *
 * Полнотекстовый поиск по всем проектам пользователя:
 * - Строка поиска со знаком «+» для добавления проектов в контекст поиска
 * - Без выбранных проектов поиск идёт по ВСЕМ проектам
 * - Клик по результату -> переход к проекту, где найдено совпадение
 */

import { useState, useRef, useEffect, useCallback, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout } from '../components/Layout/Layout'
import { Card } from '../components/Common/Card'
import {
  Search, Plus, X, Loader, FileText, Package, Zap, Wrench, Check,
  FolderOpen, ChevronRight, AlertCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import { technicalCardAPI, projectsAPI } from '../services/api'

const SHEET_TYPE_CONFIG = {
  materials: { label: 'Материалы', color: 'bg-blue-100 text-blue-800', icon: Package },
  assembly: { label: 'Сборка', color: 'bg-green-100 text-green-800', icon: Wrench },
  operational: { label: 'Операции', color: 'bg-purple-100 text-purple-800', icon: Zap },
  unknown: { label: 'Неизвестно', color: 'bg-gray-100 text-gray-800', icon: FileText },
}

// Рендер headline с подсветкой [[слово]]
const HighlightedText = ({ text }) => {
  if (!text) return null
  const parts = text.split(/(\[\[.*?\]\])/g)
  return (
    <>
      {parts.map((part, idx) =>
        part.startsWith('[[') && part.endsWith(']]') ? (
          <mark key={idx} className="bg-yellow-200 text-gray-900 font-semibold rounded px-0.5">
            {part.slice(2, -2)}
          </mark>
        ) : (
          <Fragment key={idx}>{part}</Fragment>
        )
      )}
    </>
  )
}

export const DocumentSearchPage = () => {
  const navigate = useNavigate()

  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchData, setSearchData] = useState(null)

  // Контекст поиска: выбранные проекты
  const [contextProjects, setContextProjects] = useState([]) // [{id, name}]
  const [showProjectPicker, setShowProjectPicker] = useState(false)
  const [allProjects, setAllProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(false)

  const pickerRef = useRef(null)
  const inputRef = useRef(null)

  // Автофокус на строке поиска при открытии страницы
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Закрытие пикера проектов по клику снаружи
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowProjectPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Загрузка списка проектов при открытии пикера
  const openProjectPicker = useCallback(async () => {
    setShowProjectPicker(prev => !prev)

    if (allProjects.length === 0) {
      setLoadingProjects(true)
      try {
        const response = await projectsAPI.list()
        setAllProjects(response.data.projects || [])
      } catch (error) {
        console.error('Load projects error:', error)
        toast.error('Не удалось загрузить список проектов')
      } finally {
        setLoadingProjects(false)
      }
    }
  }, [allProjects.length])

  const toggleProjectInContext = (project) => {
    setContextProjects(prev => {
      const exists = prev.some(p => p.id === project.id)
      if (exists) {
        return prev.filter(p => p.id !== project.id)
      }
      return [...prev, { id: project.id, name: project.file_name }]
    })
  }

  const removeFromContext = (projectId) => {
    setContextProjects(prev => prev.filter(p => p.id !== projectId))
  }

  const performSearch = async () => {
    const trimmed = query.trim()
    if (!trimmed) {
      toast.error('Введите поисковый запрос')
      inputRef.current?.focus()
      return
    }
    if (trimmed.length < 2) {
      toast.error('Запрос слишком короткий (минимум 2 символа)')
      return
    }

    setShowProjectPicker(false)
    setSearching(true)
    try {
      const cardIds = contextProjects.map(p => p.id)
      const response = await technicalCardAPI.searchMultiDocuments(trimmed, cardIds, 10)
      setSearchData(response.data)

      if ((response.data.projects || []).length === 0) {
        toast('Ничего не найдено. Попробуйте изменить запрос.', { icon: '🔍' })
      }
    } catch (error) {
      console.error('Document search error:', error)
      toast.error(error.response?.data?.detail || 'Ошибка поиска по документам')
    } finally {
      setSearching(false)
    }
  }

  // Переход к проекту, где найдено совпадение
  const handleResultClick = (cardId) => {
    navigate(`/projects/${cardId}`)
  }

  const projects = searchData?.projects || []

  return (
    <Layout>
      <div className="space-y-6">
        {/* Заголовок страницы */}
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Search size={28} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Поиск по документу</h1>
            <p className="text-sm text-gray-600">
              Полнотекстовый поиск по материалам, операциям, оборудованию и сборкам
              во всех ваших проектах
            </p>
          </div>
        </div>

        {/* Строка поиска */}
        <Card>
          <div ref={pickerRef} className="relative">
            <div className="flex items-center gap-2 border-2 rounded-xl px-3 py-2 focus-within:border-blue-500 border-gray-200 transition-colors">
              {/* Кнопка "+" - добавление проектов в контекст */}
              <button
                onClick={openProjectPicker}
                title="Добавить проект в контекст поиска"
                className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                  showProjectPicker || contextProjects.length > 0
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Plus size={20} />
              </button>

              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && performSearch()}
                placeholder="Например: болт М10, сверление, лестница, ГОСТ 7798..."
                maxLength={200}
                disabled={searching}
                className="flex-1 min-w-0 bg-transparent text-base text-gray-900 placeholder-gray-400 focus:outline-none disabled:opacity-60"
              />

              {query && !searching && (
                <button
                  onClick={() => { setQuery(''); setSearchData(null); inputRef.current?.focus() }}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Очистить"
                >
                  <X size={18} />
                </button>
              )}

              <button
                onClick={performSearch}
                disabled={searching || !query.trim()}
                className="flex-shrink-0 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
              >
                {searching ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Search size={16} />
                )}
                Найти
              </button>
            </div>

            {/* Выпадающий пикер проектов */}
            {showProjectPicker && (
              <div className="absolute left-0 right-0 top-full mt-2 z-40 bg-white border rounded-xl shadow-lg max-h-72 overflow-y-auto">
                <div className="px-4 py-2.5 border-b bg-gray-50 rounded-t-xl sticky top-0">
                  <p className="text-sm font-medium text-gray-700">
                    Выберите проекты для поиска:
                  </p>
                </div>

                {loadingProjects ? (
                  <div className="flex justify-center py-6">
                    <Loader size={22} className="animate-spin text-blue-600" />
                  </div>
                ) : allProjects.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">
                    У вас пока нет проектов
                  </p>
                ) : (
                  <ul className="py-1">
                    {allProjects.map((project) => {
                      const isSelected = contextProjects.some(p => p.id === project.id)
                      return (
                        <li key={project.id}>
                          <button
                            onClick={() => toggleProjectInContext(project)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                              isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <span className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                            }`}>
                              {isSelected && <Check size={13} className="text-white" />}
                            </span>
                            <FolderOpen size={16} className="flex-shrink-0 text-gray-400" />
                            <span className="truncate font-medium">{project.file_name}</span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Чипы выбранных проектов (контекст поиска) */}
          <div className="mt-3">
            {contextProjects.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">Поиск в:</span>
                {contextProjects.map((p) => (
                  <span
                    key={p.id}
                    className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-full"
                  >
                    <span className="truncate max-w-[220px]">{p.name}</span>
                    <button
                      onClick={() => removeFromContext(p.id)}
                      className="p-0.5 hover:bg-blue-200 rounded-full transition-colors"
                      title="Убрать из контекста"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Поиск выполняется по всем вашим проектам. Нажмите «+», чтобы ограничить конкретными проектами.
              </p>
            )}
          </div>

          {/* Сводка результатов */}
          {searchData && !searching && (
            <div className="mt-3 pt-3 border-t text-sm text-gray-600">
              {projects.length > 0 ? (
                <span>
                  По запросу <span className="font-semibold text-gray-900">«{searchData.query}»</span>{' '}
                  найдено совпадений: <span className="font-semibold text-blue-600">{searchData.total_matches}</span>{' '}
                  в <span className="font-semibold text-blue-600">{projects.length}</span>{' '}
                  {projects.length === 1 ? 'проекте' : 'проектах'}
                </span>
              ) : (
                <span>
                  По запросу <span className="font-semibold text-gray-900">«{searchData.query}»</span> ничего не найдено
                </span>
              )}
            </div>
          )}
        </Card>

        {/* Индикатор загрузки */}
        {searching && (
          <Card>
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader size={36} className="animate-spin text-blue-600" />
              <p className="text-sm text-gray-600">Поиск по документам...</p>
            </div>
          </Card>
        )}

        {/* Результаты: группировка по проектам */}
        {!searching && projects.length > 0 && (
          <div className="space-y-4">
            {projects.map((project) => (
              <Card key={project.card_id} className="hover:shadow-md transition-shadow">
                {/* Шапка проекта - клик ведёт к проекту */}
                <button
                  onClick={() => handleResultClick(project.card_id)}
                  className="w-full flex items-center gap-3 text-left group mb-3"
                >
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                    <FolderOpen size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                      {project.project_name}
                    </h3>
                    <p className="text-xs text-gray-500">
                      Найдено листов: {project.sheets.length} · релевантность до {(project.max_rank * 100).toFixed(0)}%
                    </p>
                  </div>
                  <span className="flex-shrink-0 inline-flex items-center gap-1 text-sm text-blue-600 font-medium group-hover:underline">
                    Открыть проект
                    <ChevronRight size={16} />
                  </span>
                </button>

                {/* Найденные листы проекта */}
                <div className="space-y-2 pl-2 border-l-2 border-blue-100">
                  {project.sheets.map((sheet) => {
                    const typeConfig = SHEET_TYPE_CONFIG[sheet.sheet_type] || SHEET_TYPE_CONFIG.unknown
                    const TypeIcon = typeConfig.icon

                    return (
                      <button
                        key={sheet.sheet_id}
                        onClick={() => handleResultClick(project.card_id)}
                        className="w-full flex gap-3 p-3 text-left rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {/* Миниатюра листа */}
                        <div className="flex-shrink-0">
                          {sheet.image_url ? (
                            <img
                              src={sheet.image_url}
                              alt={`Лист ${sheet.page_number}`}
                              className="w-16 h-20 object-cover rounded border bg-gray-50"
                              loading="lazy"
                              onError={(e) => { e.target.style.display = 'none' }}
                            />
                          ) : (
                            <div className="w-16 h-20 flex items-center justify-center rounded border bg-gray-50">
                              <FileText size={20} className="text-gray-300" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-medium text-gray-900 text-sm">
                              Лист {sheet.page_number}
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${typeConfig.color}`}>
                              <TypeIcon size={11} />
                              {typeConfig.label}
                            </span>
                            <span className="text-xs text-gray-400">
                              {(sheet.rank * 100).toFixed(0)}%
                            </span>
                          </div>

                          {/* Подсветка совпадений */}
                          {sheet.headline && (
                            <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                              <HighlightedText text={sheet.headline} />
                            </p>
                          )}

                          {/* Найденные позиции */}
                          {sheet.matches && sheet.matches.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {sheet.matches.slice(0, 4).map((match, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-600"
                                  title={match.text}
                                >
                                  <span className="font-medium">{match.context}:</span>
                                  <span className="truncate max-w-[200px]">{match.text}</span>
                                </span>
                              ))}
                              {sheet.matches_total > 4 && (
                                <span className="text-xs text-gray-400 px-1 py-0.5">
                                  +{sheet.matches_total - 4}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Пустая выдача */}
        {!searching && searchData && projects.length === 0 && (
          <Card>
            <div className="text-center py-12">
              <AlertCircle size={44} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-700 font-medium mb-1">Ничего не найдено</p>
              <p className="text-sm text-gray-500">
                Попробуйте изменить запрос или проверьте, что листы проектов проанализированы.
              </p>
            </div>
          </Card>
        )}

        {/* Начальное состояние */}
        {!searching && !searchData && (
          <Card>
            <div className="text-center py-12">
              <Search size={44} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-700 font-medium mb-1">
                Введите запрос для поиска по документам
              </p>
              <p className="text-sm text-gray-500">
                Поиск с учётом русской морфологии: «болты» найдёт «болт М10».
                Ищет по материалам, операциям, оборудованию, компонентам и этапам сборки.
              </p>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  )
}

export default DocumentSearchPage
